/**
 * The WHS panels' pure half (13-28): when an incident has a WHS branch, the WHS verb's columns in the order HR & WHS
 * answers them (one question at a time), each group's "why" with its `12.1-OBLIGATIONS.md` rows, the "by" days of the
 * timed duties as the form stands, and when the WorkSafe notice panel shows.
 * `components/record/panels/WhsPanel.tsx` and `WorkSafePanel.tsx` render it; 13-20's engine decides every save
 * (`buildRecordVerb('whs' | 'notification', …)`) and `lib/notificationDeadlines` every deadline.
 *
 * Nothing here retypes a column list or a choice: the groups are asserted at load to be exactly the WHS verb's
 * allow-list (13-20, FIELDSET §3.3.1's 22 columns), and every value a rule names goes through `liveChoice`. Every OB
 * id cited is checked against the matrix by `whsPanels.test.ts`. Pure: no React, no Graph, no `Date.now()`.
 */

import { liveChoice } from '../config/fieldset/live';
import { verbColumns } from '../config/fieldset/registerFields';
import type { RecordRow } from '../types/records';
import { melbourneWallToInstant } from './fieldValues';
import { notificationDue, whsDayDeadlines, type DayDeadline, type DueResult } from './notificationDeadlines';
import { noticesFor } from './regulatoryPanels';
import { timeKeyOf } from './verbForm';

const I = 'incidents';

const [WORKER_YES] = liveChoice(I, 'WorkerInjured', 'Yes');
const [STAFF] = liveChoice(I, 'BusinessUnit', 'Staff');
const [HAZARD] = liveChoice(I, 'Category', 'Other - Hazard, Near miss');
const [WORKSAFE_DECISION] = liveChoice(I, 'ReportabilityDecision', 'WorkSafe');
const [WHS_RISK] = liveChoice('risk', 'RiskCategory', 'WHS');

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

// ---------------------------------------------------------------------------
// When the WHS branch opens (D-13-28-6)
// ---------------------------------------------------------------------------

/**
 * Why this incident has a WHS branch, in words (empty = none). The plan's four conditions, plus a `WorkSafe`
 * reportability decision (D-13-28-6): a call that WorkSafe must be told is a WHS incident by definition.
 */
export function whsBranchReasons(row: RecordRow): string[] {
  if (row.register !== 'incidents') return [];
  const f = row.fields;
  const out: string[] = [];
  if (text(f.WorkerInjured) === WORKER_YES) out.push('a worker was injured');
  if (text(f.WorkSafeNotifiable) !== '') out.push('the WorkSafe question has been answered');
  if (text(f.BusinessUnit) === STAFF) out.push('the business unit is Staff');
  if (text(f.Category) === HAZARD) out.push(`the category is “${HAZARD}”`);
  if (text(f.ReportabilityDecision) === WORKSAFE_DECISION) out.push('the reportability decision is WorkSafe');
  return out;
}

/** A Risk in the `WHS` category (FIELDSET §2.4: `HazardType` is required then). */
export function isWhsRisk(row: RecordRow): boolean {
  return row.register === 'risk' && text(row.fields.RiskCategory) === WHS_RISK;
}

/** The WorkSafe notice panel shows when the WHS answer is Yes or the decision is WorkSafe (the 13-27 rule). */
export function isWorkSafeNotice(row: RecordRow): boolean {
  return row.register === 'incidents' && noticesFor(row).includes('worksafe');
}

// ---------------------------------------------------------------------------
// The groups, one question at a time
// ---------------------------------------------------------------------------

/** One obligation row a group serves: the id and the instrument cell's section, as `12.1-OBLIGATIONS.md` has them. */
export interface WhyRow {
  ob: string;
  section: string;
  /** What the row asks, in plain words. */
  says: string;
}

export interface WhsGroup {
  key: string;
  /** The question, as a heading. */
  question: string;
  columns: readonly string[];
  why: readonly WhyRow[];
}

/** The plan's eight groups, in order. `NotificationDueAt` is computed (shown under `AwareAt`), so it is no input. */
export const WHS_GROUPS: readonly WhsGroup[] = [
  {
    key: 'notifiable',
    question: 'Is it notifiable?',
    columns: ['WorkSafeNotifiable', 'WorkSafeIncidentKind', 'WorkplaceType', 'UnderHarmonyControl', 'AwareAt'],
    why: [
      { ob: 'OB-OHS-01', section: 'OHSA-VIC-2004-NOTIF s 37(1)-(3)', says: 'decide whether Part 5 applies. A blank is not "no".' },
      { ob: 'OB-OHS-02', section: 'OHSA-VIC-2004-NOTIF s 37(2)', says: 'classify the s 37 kind, in the Act’s words.' },
      { ob: 'OB-OHS-03', section: 'OHSA-VIC-2004-NOTIF s 38(1)', says: 'the duty is for a workplace under Harmony’s management and control. The Act does not say whether a client’s home is one; that is an assessment.' },
      { ob: 'OB-OHS-04', section: 'OHSA-VIC-2004-NOTIF s 38(1)', says: 'when Harmony became aware starts the clock.' },
      { ob: 'OB-OHS-05', section: 'OHSA-VIC-2004-NOTIF s 38(1)', says: 'notify WorkSafe immediately after becoming aware.' },
    ],
  },
  {
    key: 'worker',
    question: 'Who is the injured worker?',
    // 13.2 (D-13.2-P-2): the WHS verb records the worker by name; `InjuredWorkerUPN` is FieldLink's self-report only.
    columns: ['InjuredWorkerName'],
    why: [{ ob: 'OB-OHS-17', section: 'WIRCA-VIC-2013 s 3', says: 'the register of injuries names the injured worker. Recorded by their name (D-13.2-P-2).' }],
  },
  {
    key: 'notice',
    question: 'Has a notice of injury been received?',
    columns: ['InjuryNoticeReceivedOn', 'InjuryNoticeMethod', 'InjuryNoticeAcknowledgedOn'],
    why: [
      { ob: 'OB-OHS-24', section: 'WIRCA-VIC-2013 s 17(3)', says: 'on receiving notice of an injury not yet in the register, enter its particulars.' },
      { ob: 'OB-OHS-25', section: 'WIRCA-VIC-2013 s 18(2)(a)', says: 'how the notice was given.' },
      { ob: 'OB-OHS-26', section: 'WIRCA-VIC-2013 s 18(4)', says: 'acknowledge the notice in writing. No period is set.' },
    ],
  },
  {
    key: 'claim',
    question: 'Has a claim been received?',
    columns: ['ClaimReceivedOn', 'ClaimType', 'ClaimAcknowledgedOn', 'ClaimForwardedOn'],
    why: [
      { ob: 'OB-OHS-29', section: 'WIRCA-VIC-2013 s 73(1)', says: 'the day the claim was received starts the forwarding clock.' },
      { ob: 'OB-OHS-30', section: 'WIRCA-VIC-2013 s 73(1)(a)-(j)', says: 'classify the claim, in the Act’s words.' },
      { ob: 'OB-OHS-31', section: 'WIRCA-VIC-2013 s 21(1)', says: 'acknowledge the claim to the worker in writing as soon as reasonably practicable.' },
      { ob: 'OB-OHS-33', section: 'WIRCA-VIC-2013 s 73(1)', says: 'forward the claim to WorkSafe within 10 days after receipt.' },
    ],
  },
  {
    key: 'medical',
    question: 'Has a medical certificate been received?',
    columns: ['MedicalCertificateReceivedOn', 'MedicalCertificateForwardedOn'],
    why: [{ ob: 'OB-OHS-35', section: 'WIRCA-VIC-2013 s 73(1)(b)', says: 'forward the medical certificate with or after a weekly-payments claim, within 10 days.' }],
  },
  {
    key: 'mental',
    question: 'Is it a mental injury claim?',
    columns: ['MentalInjuryClaim', 'MentalInjuryNotifiedOn'],
    why: [
      { ob: 'OB-OHS-36', section: 'WIRCA-VIC-2013 s 73A(1)', says: 'identify a claim for a mental injury.' },
      { ob: 'OB-OHS-37', section: 'WIRCA-VIC-2013 s 73A(1)', says: 'notify WorkSafe of it within 3 business days after receipt, as well as forwarding it.' },
    ],
  },
  {
    key: 'rtw',
    question: 'Return to work',
    columns: ['RtwKnowledgeOn', 'RtwPlanningStartedOn'],
    why: [{ ob: 'OB-OHS-39', section: 'WIRCA-VIC-2013 s 104(1)', says: 'plan return to work from when Harmony knew, or ought reasonably to have known, of the incapacity.' }],
  },
  {
    key: 'allegation',
    question: 'Is there an allegation against a worker?',
    columns: ['AllegationAgainstWorker', 'AllegationFirstHeldOn'],
    why: [
      { ob: 'OB-HR-54', section: 'NDIS-WS-RULES-2018 s 18(5)(a)', says: 'keep a record of a misconduct allegation against a worker (NDIS).' },
      { ob: 'OB-HR-57', section: 'ACR-2025-s154-915 (1)(a)', says: 'keep a record of a misconduct allegation, and the day it was first held (aged care).' },
    ],
  },
];

/** The computed column the first group shows under `AwareAt`. */
export const WHS_COMPUTED = 'NotificationDueAt';

/** Every WHS input, in group order. */
export const WHS_INPUTS: readonly string[] = WHS_GROUPS.flatMap((g) => g.columns);

// Load-time check: the groups are exactly the WHS verb's allow-list (13-20), each column once.
{
  const allow = verbColumns(I, 'whs');
  const grouped = [...WHS_INPUTS, WHS_COMPUTED];
  if (new Set(grouped).size !== grouped.length || grouped.length !== allow.length || !allow.every((c) => grouped.includes(c))) {
    throw new Error('whsPanels: the WHS groups are not the WHS verb’s allow-list');
  }
}

/** The group a column opens (its first column), for the heading. */
export function groupStartingAt(column: string): { group: WhsGroup; index: number } | null {
  const index = WHS_GROUPS.findIndex((g) => g.columns[0] === column);
  return index === -1 ? null : { group: WHS_GROUPS[index], index };
}

// ---------------------------------------------------------------------------
// The "by" days, as the form stands
// ---------------------------------------------------------------------------

/** A WHS day duty against the form: done (stored or typed), or overdue (amber, never red, D-13-15-4). */
export interface DayState {
  rule: DayDeadline;
  done: string | null;
  overdue: boolean;
}

/** The form's value for a column when it has one (an empty input means cleared), else the record's. */
function current(row: RecordRow, values: Readonly<Record<string, string>>, column: string): string | null {
  if (column in values) return values[column] === '' ? null : values[column];
  const v = row.fields[column];
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Each timed WHS duty whose anchor is recorded (or typed), with its done day and whether it is overdue on `today`. */
export function whsDayStates(row: RecordRow, values: Readonly<Record<string, string>>, today: string): DayState[] {
  const rules = whsDayDeadlines({
    ClaimReceivedOn: current(row, values, 'ClaimReceivedOn'),
    MedicalCertificateReceivedOn: current(row, values, 'MedicalCertificateReceivedOn'),
    MentalInjuryClaim: current(row, values, 'MentalInjuryClaim'),
    RtwKnowledgeOn: current(row, values, 'RtwKnowledgeOn'),
  });
  return rules.map((rule) => {
    const done = rule.notice ? current(row, values, rule.notice) : null;
    return { rule, done, overdue: done === null && today > rule.dueOn };
  });
}

/** The "by" word for each duty, as the panel reads it. */
export const BY_WORD: Readonly<Record<string, string>> = {
  ClaimForwardedOn: 'Forward by',
  MedicalCertificateForwardedOn: 'Forward by',
  MentalInjuryNotifiedOn: 'Notify by',
  RtwPlanningStartedOn: 'Start planning by',
};

/** Duties with no fixed period: a plain line, never amber (the row sets no day). */
export const NO_FIXED_DAY: Readonly<Record<string, WhyRow>> = {
  ClaimAcknowledgedOn: { ob: 'OB-OHS-31', section: 'WIRCA-VIC-2013 s 21(1)', says: 'Acknowledge in writing as soon as reasonably practicable. No fixed day.' },
  InjuryNoticeAcknowledgedOn: { ob: 'OB-OHS-26', section: 'WIRCA-VIC-2013 s 18(4)', says: 'Acknowledge in writing. The Act sets no period.' },
};

/**
 * `NotificationDueAt` as the WHS form stands (the engine computes the same at save, D-13-28-1): the form's `AwareAt`
 * and WorkSafe answer, with the reportability call and NDIS class from the record (the WHS verb never changes them).
 */
export function whsFormDue(row: RecordRow, values: Readonly<Record<string, string>>): DueResult {
  const day = values.AwareAt ?? '';
  const time = values[timeKeyOf('AwareAt')] ?? '';
  let awareAt: string | null = null;
  if (day && time) {
    const r = melbourneWallToInstant(day, time);
    awareAt = r.ok ? r.value : null;
  } else if (!day && !time && !('AwareAt' in values)) {
    awareAt = text(row.fields.AwareAt) || null;
  }
  return notificationDue({
    awareAt,
    decision: text(row.fields.ReportabilityDecision) || null,
    ndisNoticeClass: text(row.fields.NdisNoticeClass) || null,
    workSafeNotifiable: current(row, values, 'WorkSafeNotifiable'),
  });
}

// ---------------------------------------------------------------------------
// The site-preservation row (OB-OHS-13…15; FIELDSET §3.4.1 G-179)
// ---------------------------------------------------------------------------

/** The s 39(2) disturbances the Act permits, in OB-OHS-15's words. */
export const PERMITTED_DISTURBANCES: readonly string[] = [
  "protect a person's health or safety",
  'aid an injured person',
  'essential action to make safe or prevent recurrence',
];

/** The WorkSafe written-record copy's name (FIELDSET §2.1 `Attachments`, D-13-L-6). */
export function worksafeRecordName(ref: string): string {
  return `${ref}-worksafe-record`;
}
